/*
  ##Custom Code##
  Warenkorb (Prestige-Warenkorb statt UpCart) für das Live-Theme, wie im NEUEN TEMPLATE (dort in custom.js).
  Geladen in layout/theme.liquid nach theme.js.
*/
import { CartDrawer, Drawer, QuickBuyModal } from 'theme';

// Quick-Buy („Optionen auswählen“) öffnet als Seitenleiste von rechts, genau wie der Warenkorb (wie im NEUEN TEMPLATE).
// Beide Elemente nutzen dieselbe Shadow-DOM-Struktur, daher reicht es, Vorlage und Animation des Drawers auf das
// Quick-Buy-Modal zu übertragen. Die Karten setzen dazu die Klasse "drawer" (snippets/product-card*.liquid).
const drawerBehavior = Object.getOwnPropertyDescriptors(Drawer.prototype);

['shadowDomTemplate', 'openFrom', 'createEnterAnimationControls', 'createLeaveAnimationControls'].forEach((key) => {
  if (drawerBehavior[key]) {
    Object.defineProperty(QuickBuyModal.prototype, key, drawerBehavior[key]);
  }
});

// Gratisversand-Balken: Die Breite kommt aus Liquid. Ändert sich der Warenkorb ohne Neu-Rendern,
// setzt Prestige nur das Attribut total-price neu, deshalb ziehen wir die Breite hier nach.
document.addEventListener('cart:change', () => {
  document.querySelectorAll('free-shipping-bar').forEach((bar) => {
    const fill = bar.querySelector('.free-shipping-bar__fill');
    const threshold = parseFloat(bar.getAttribute('threshold'));

    if (fill && threshold > 0) {
      fill.style.width = `${Math.min(100, (bar.totalPrice / threshold) * 100)}%`;
    }
  });
});

// Warenkorb und Quick-Buy am Desktop als angedockte Seitenleiste: keine Abdunkelung, die Seite bleibt scroll- und klickbar
// und rückt um die Breite der Leiste zur Seite (Klasse "side-panel-docked", siehe occulto-warenkorb.css). Unter 1570px
// (Laptops, Tablet, Handy) normale Fenster mit Abdunkelung, sonst wird die Seite zu schmal.
const sidePanelDocked = window.matchMedia('(min-width: 1570px)');
const sidePanelSelector = 'quick-buy-modal, cart-drawer';

[QuickBuyModal, CartDrawer].forEach((PanelClass) => {
  Object.defineProperties(PanelClass.prototype, {
    shouldLock: { configurable: true, get: () => !sidePanelDocked.matches },
    shouldTrapFocus: { configurable: true, get: () => !sidePanelDocked.matches }
  });
});

const updateSidePanelDocked = () => {
  const isOpen = sidePanelDocked.matches && [...document.querySelectorAll(sidePanelSelector)].some((panel) => panel.open);
  document.documentElement.classList.toggle('side-panel-docked', isOpen);
};

document.addEventListener('dialog:before-show', (event) => {
  const panel = event.target;
  if (!panel.matches?.(sidePanelSelector) || !sidePanelDocked.matches) return;

  panel.setAttribute('aria-modal', 'false');

  // Immer nur ein Quick-Buy offen
  if (panel.matches('quick-buy-modal')) {
    document.querySelectorAll('quick-buy-modal[open]').forEach((modal) => modal !== panel && modal.hide());
  }

  updateSidePanelDocked();
}, true);

document.addEventListener('dialog:before-hide', (event) => {
  if (!event.target.matches?.(sidePanelSelector)) return;
  requestAnimationFrame(updateSidePanelDocked);
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !document.documentElement.classList.contains('side-panel-docked')) return;
  document.querySelectorAll(sidePanelSelector).forEach((panel) => panel.open && panel.hide());
});

sidePanelDocked.addEventListener('change', () => {
  document.querySelectorAll(sidePanelSelector).forEach((panel) => panel.open && panel.hide());
});

// Preis wie im Sale: Ist eine Mengenstufe mit Rabatt gewählt (Fly-Bundler auf der Produktseite oder Bundler im Quick-Buy),
// zeigt der Preis oben den Gesamtpreis der Stufe rot mit „Statt:“ und Streichpreis, „nur … / Paar“ rechnet mit.
// Die Original-Elemente bleiben im Preis-Block und werden nur versteckt, ohne Rabattstufe kommen sie zurück.
const parseMoney = (text) => Math.round(parseFloat((text || '').replace(/[^0-9,]/g, '').replace(',', '.')) * 100) || 0;

const pricePerUnitText = (element, total, boxes) => {
  const packSize = parseFloat(element.dataset.packSize);
  if (!packSize) return null;
  // Normal auf volle Cent gerundet; ergibt das ,00 oder ,50, einen Cent weniger (wie in price-list.liquid)
  let cents = Math.round(total / (boxes * packSize) + 1e-9);
  if (cents > 0 && cents % 50 === 0) cents -= 1;
  return `nur ${formatMoney(cents)} / ${element.dataset.packUnit}`;
};

const showTierPrice = (priceList, tier, updateUnitPrice = true) => {
  if (!priceList) return;
  const discounted = tier && tier.original > tier.total;
  const key = discounted ? `${tier.total}-${tier.original}` : '';
  if ((priceList.dataset.occultoTier || '') === key) return;
  priceList.dataset.occultoTier = key;

  priceList.querySelectorAll('[data-occulto-tier-price]').forEach((element) => element.remove());
  const originals = priceList.querySelectorAll(':scope > sale-price, :scope > compare-at-price, :scope > .compare-label');
  originals.forEach((element) => (element.hidden = discounted));

  // „nur … / Paar“: auf der Produktseite rechnet das Skript in theme.liquid, hier nur im Quick-Buy
  const unitPrice = updateUnitPrice && priceList.querySelector('.unit-price-extra[data-pack-size]');
  if (unitPrice) {
    unitPrice.dataset.occultoText ??= unitPrice.textContent;
    unitPrice.textContent = discounted ? pricePerUnitText(unitPrice, tier.total, tier.boxes) || unitPrice.dataset.occultoText : unitPrice.dataset.occultoText;
  }

  if (!discounted) return;

  // Gleiche Klassen wie Prestige im Sale (price-list.liquid): Produktseite h4/h5, Karten und Quick-Buy ohne
  const saleClass = (originals[0]?.className || '').replace('text-subdued', '').trim();
  const compareClass = `${saleClass.includes('h4') ? 'h5 ' : saleClass.includes('h6') ? 'h6 ' : ''}text-subdued line-through`;
  originals[0]?.insertAdjacentHTML('beforebegin', `<sale-price class="${saleClass.replace('text-on-sale', '')} text-on-sale" data-occulto-tier-price>
      <span class="sr-only">Angebotspreis</span>${formatMoney(tier.total)}</sale-price><span class="compare-label" data-occulto-tier-price>Statt: </span><compare-at-price class="${compareClass}" data-occulto-tier-price>
      <span class="sr-only">Normaler Preis</span>${formatMoney(tier.original)}</compare-at-price>`);
};

// Produktseite: Fly zeichnet Stufen und Prestige den Preis bei jeder Auswahl neu, deshalb auf Änderungen reagieren
const updateProductPageTierPrice = () => {
  document.querySelectorAll('.product-info').forEach((info) => {
    if (info.closest('quick-buy-modal, [data-occulto-bundle-scope]')) return;
    const scope = info.closest('.shopify-section') || document;
    const selected = scope.querySelector('[data-offer-id][data-qty-label]:has(input[type="radio"]:checked)');
    if (!selected) return;
    showTierPrice(info.querySelector('price-list'), {
      boxes: parseFloat(selected.dataset.qtyLabel) || 1,
      total: parseMoney(selected.querySelector('.gfbVolumeDiscountDiscountPrice')?.textContent),
      original: parseMoney(selected.querySelector('.gfbVolumeDiscountOriginalPrice')?.textContent)
    }, false);
  });
};

if (document.querySelector('.product-info')) {
  let tierPriceFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(tierPriceFrame);
    tierPriceFrame = requestAnimationFrame(updateProductPageTierPrice);
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'class'] });

  // Fly setzt die Auswahl per Skript und stoppt den Klick, deshalb in der Capture-Phase mithören
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-offer-id][data-qty-label]')) return;
    setTimeout(updateProductPageTierPrice, 50);
    setTimeout(updateProductPageTierPrice, 400);
  }, true);
}

// Mengen-Bundler im Quick-Buy (snippets/product-quick-buy.liquid): Stufen aus den Daten von Fly Bundles
// (window.fly_app_data, auf jeder Seite vorhanden), wie der Bundler auf der Produktseite. Die gewählte Stufe ist die Menge.
// Ab der zweiten Stufe bekommt die Zeile dieselben Eigenschaften wie bei Fly, sonst greift der Mengenrabatt nicht.
// Die Felder hängen per form-Attribut am Formular, Prestige schickt sie beim Hinzufügen mit.
const formatMoney = (cents) => window.themeVariables.settings.moneyFormat
  .replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2).replace('.', ','));

const flyVolumeOffer = (productId) => (window.fly_app_data?.data?.volumeBundleData || []).find((offer) => offer.isActive !== false
  && offer.discountType === 'PERCENTAGE'
  && (offer.widgetDisplayOptions?.specificProducts || []).some((product) => String(product.productId) === String(productId)));

const selectQuickBundleTier = (box, index) => {
  const rules = flyVolumeOffer(box.dataset.productId)?.rules || [];
  const rule = rules[index];
  const quantity = Number(rule?.conditionValue) || index + 1;
  const discounted = index > 0 && Number(rule?.discountValue) > 0;
  const field = (name) => box.querySelector(`input[name="${name}"]`);

  field('quantity').value = quantity;

  [['properties[_flyBundles:OfferId]', box.dataset.offerKey], ['properties[_flyBundles:prodQty]', quantity], ['properties[_flyBundles:selectedTier]', index]]
    .forEach(([name, value]) => {
      field(name).value = value;
      field(name).disabled = !discounted; // wie Fly: ohne Rabattstufe keine Eigenschaften
    });

  // Gewählte Menge in der Überschrift, Preis im Kopf wie im Sale
  box.querySelector('[data-bundle-selected]').textContent = rule?.textConfig?.title || quantity;

  const [price, compare] = JSON.parse(box.querySelector('script').textContent)[box.dataset.variantId] || [];
  const percent = Number(rule?.discountValue) || 0;
  if (price != null) {
    // Quick-Buy: Preis im Kopf; Produkt-Highlight der Startseite (sections/occulto-produkt-highlight.liquid): Preis der Kaufbox
    const priceLists = box.closest('quick-buy-modal')?.querySelectorAll('.quick-buy-modal__header-text price-list')
      || box.closest('[data-occulto-bundle-scope]')?.querySelectorAll('.product-info price-list') || [];
    priceLists.forEach((priceList) => showTierPrice(priceList, {
      boxes: quantity,
      total: Math.round(price * quantity * (1 - percent / 100)),
      original: percent > 0 ? Math.max(price, compare || 0) * quantity : 0
    }));
  }
};

const renderQuickBundle = (box, variantId) => {
  const offer = flyVolumeOffer(box.dataset.productId);
  const form = document.forms[box.dataset.formId];
  const prices = JSON.parse(box.querySelector('script').textContent);
  box.dataset.variantId = variantId || form?.elements.id?.value || '';
  const [price, compare] = prices[box.dataset.variantId] || [];
  const rules = offer?.rules || [];

  // Warenkorb-Button in eine feste Fußzeile der Leiste (occulto-warenkorb.css), der Bundler steht darüber und scrollt mit.
  // Das Teil-Neuzeichnen beim Variantenwechsel ersetzt nur den Button in seinem Block, die Hülle bleibt stehen.
  const buyBlock = box.closest('.quick-buy-modal__info-wrapper, [data-occulto-bundle-scope]')?.querySelector('[data-block-type="buy-buttons"]');
  if (buyBlock && box.closest('quick-buy-modal') && !buyBlock.parentElement.matches('.occulto-quick-buy-footer')) {
    const footer = document.createElement('div');
    footer.className = 'occulto-quick-buy-footer';
    buyBlock.before(footer);
    footer.append(buyBlock);
  }
  (buyBlock?.parentElement.matches('.occulto-quick-buy-footer') ? buyBlock.parentElement : buyBlock)?.before(box);

  if (!form || rules.length < 2 || price == null) {
    box.hidden = true;
    return;
  }

  const selected = Number(box.querySelector('input[type="radio"]:checked')?.value ?? offer.activeTierIndex ?? 0);
  box.dataset.offerKey ??= `${offer.offerId || offer.metafieldData?.offerId}_${Math.random().toString(36).slice(2, 5).toUpperCase()}`;

  const tiers = rules.map((rule, index) => ({
    index,
    percent: Number(rule.discountValue) || 0,
    title: rule.textConfig?.title || Number(rule.conditionValue) || index + 1
  }));

  box.innerHTML = `${box.querySelector('script').outerHTML}
    <p class="occulto-quick-bundle__title">${offer.title || 'Menge'}: <strong data-bundle-selected></strong></p>
    <div class="occulto-quick-bundle__tiers" style="--occulto-tiers: ${tiers.length}">
      ${tiers.map((tier) => `<label class="occulto-quick-bundle__tier">
        <input type="radio" class="sr-only" name="occulto-quick-bundle-${box.dataset.formId}" value="${tier.index}" ${tier.index === selected ? 'checked' : ''}>
        <span class="occulto-quick-bundle__name">${tier.title}</span>
        ${tier.percent > 0 ? `<span class="occulto-quick-bundle__save">Spare ${tier.percent}%</span>` : ''}
      </label>`).join('')}
    </div>
    <input type="hidden" form="${box.dataset.formId}" name="quantity">
    <input type="hidden" form="${box.dataset.formId}" name="properties[_flyBundles:OfferId]">
    <input type="hidden" form="${box.dataset.formId}" name="properties[_flyBundles:prodQty]">
    <input type="hidden" form="${box.dataset.formId}" name="properties[_flyBundles:selectedTier]">`;

  box.hidden = false;
  selectQuickBundleTier(box, selected);
};

// Auf Produktseiten baut Fly jede Anfrage mit form_type=product nach der Auswahl seines eigenen Bundlers um, auch die aus
// dem Quick-Buy (dann käme Menge und Produkt der Seite statt der Auswahl hier). Die Warenkorb-API braucht form_type nicht.
document.addEventListener('submit', (event) => {
  if (!event.target.closest?.('quick-buy-modal, [data-occulto-bundle-scope]')) return;
  event.target.querySelectorAll('input[name="form_type"], input[name="properties[_modifyPayloadQB]"]').forEach((input) => (input.disabled = true));
}, true);

document.addEventListener('change', (event) => {
  const radio = event.target.closest('[data-occulto-quick-bundle] input[type="radio"]');
  if (radio) selectQuickBundleTier(radio.closest('[data-occulto-quick-bundle]'), Number(radio.value));
});

document.addEventListener('dialog:before-show', (event) => {
  if (!event.target.matches?.('quick-buy-modal')) return;
  event.target.querySelectorAll('[data-occulto-quick-bundle]').forEach((box) => renderQuickBundle(box));
}, true);

// Andere Variante: Preise der Stufen neu. Anderes Produkt (Farbe als eigenes Produkt): Inhalt wird ersetzt, danach neu aufbauen
// (getAttribute: form.id wäre das Feld name="id" des Formulars)
document.addEventListener('variant:change', (event) => {
  document.querySelectorAll(`[data-occulto-quick-bundle][data-form-id="${event.target.getAttribute('id')}"]`)
    .forEach((box) => renderQuickBundle(box, event.detail?.variant?.id));
}, true);

// Microtask statt setTimeout: läuft, nachdem Prestige den Inhalt ersetzt hat, aber vor dem Zeichnen.
// Sonst ist beim Farbwechsel kurz der Normalpreis zu sehen.
document.addEventListener('product:rerender', () => {
  queueMicrotask(() => {
    // Neu gezeichneter Inhalt: Bundler neu aufbauen bzw. die gewählte Stufe (Preis wie im Sale) erneut setzen
    document.querySelectorAll('quick-buy-modal[open] [data-occulto-quick-bundle], [data-occulto-bundle-scope] [data-occulto-quick-bundle]').forEach((box) => {
      const radio = box.querySelector('input[type="radio"]:checked');
      if (box.hidden || !radio) renderQuickBundle(box);
      else selectQuickBundleTier(box, Number(radio.value));
    });
  });
}, true);

// Produkt-Highlight der Startseite (Handy): Pfeile blättern ein Bild weiter bzw. zurück
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-occulto-highlight-arrow]');
  const media = arrow?.parentElement.querySelector('[data-occulto-highlight-media]');
  if (media) media.scrollBy({ left: Number(arrow.dataset.occultoHighlightArrow) * media.clientWidth, behavior: 'smooth' });
});

// Produkt-Highlight der Startseite: Bundler gleich beim Laden aufbauen (im Quick-Buy erst beim Öffnen)
document.querySelectorAll('[data-occulto-bundle-scope] [data-occulto-quick-bundle]').forEach((box) => renderQuickBundle(box));

// Kollektionsseite (Filter-App Globo, snippets/globo.filter.product.liquid): „In den Warenkorb“ ist dort ein normales
// Formular, Shopify leitet danach auf die Warenkorb-Seite. Stattdessen wie die Prestige-Buttons per AJAX hinzufügen und
// dieselben Ereignisse auslösen: Das Warenkorb-Pop-up erscheint und der Warenkorb wird aktualisiert.
document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('form.spf-product__form');
  if (!form) return;

  event.preventDefault();
  event.stopPropagation();

  const button = form.querySelector('button[type="submit"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  button?.setAttribute('aria-busy', 'true');

  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

  const formData = new FormData(form);
  formData.set('sections', sections.join(','));
  formData.set('sections_url', `${Shopify.routes.root}variants/${formData.get('id')}`);

  try {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    const result = await response.json();

    // z. B. ausverkauft: Hinweis kurz im Button statt der Warenkorb-Seite
    if (!response.ok) {
      const label = button?.querySelector('span');
      if (label) {
        const text = label.textContent;
        label.textContent = result.description || 'Nicht verfügbar';
        setTimeout(() => (label.textContent = text), 3000);
      }
      return;
    }

    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart.sections = result.sections;
    form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items ?? [result], cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
  } catch {
    form.submit(); // Netzwerkfehler: wie bisher über die Warenkorb-Seite
  } finally {
    button?.removeAttribute('aria-busy');
  }
}, true);

// „Das könnte dir auch gefallen“ (snippets/occulto-cart-upsell.liquid): Variante wechseln und hinzufügen.
// Der Warenkorb wird danach neu gezeichnet und zeigt dann die nächste Empfehlung.
const showUpsellVariant = (upsell, variant) => {
  const price = upsell.querySelector('[data-upsell-price]');
  price.textContent = variant.price;
  price.classList.toggle('text-on-sale', variant.compare !== '');
  upsell.querySelector('[data-upsell-compare]').textContent = variant.compare;
  upsell.querySelectorAll('[data-upsell-link]').forEach((link) => (link.href = variant.url));

  const image = upsell.querySelector('.occulto-cart-upsell__image img');
  if (image && variant.image) {
    image.removeAttribute('srcset');
    image.src = variant.image;
  }
};

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-occulto-cart-upsell] select');
  if (!select) return;

  const upsell = select.closest('[data-occulto-cart-upsell]');
  const form = upsell.querySelector('[data-upsell-form]');
  const colorSelect = form.querySelector('[data-upsell-color]');

  // Ein Auswahlfeld mit allen Varianten
  if (!colorSelect) {
    const option = select.selectedOptions[0];
    showUpsellVariant(upsell, { ...option.dataset });
    return;
  }

  // Farbe und Größe getrennt: Größen ohne lieferbare Variante in dieser Farbe sperren, sonst nächste lieferbare Größe
  const variants = JSON.parse(form.querySelector('[data-upsell-variants]').textContent).filter(Boolean);
  const sizeSelect = form.querySelector('[data-upsell-size]');
  const sizesForColor = variants.filter((variant) => variant.color === colorSelect.value).map((variant) => variant.size);

  [...sizeSelect.options].forEach((option) => (option.disabled = !sizesForColor.includes(option.value)));
  if (!sizesForColor.includes(sizeSelect.value)) sizeSelect.value = sizesForColor[0];

  const variant = variants.find((item) => item.color === colorSelect.value && item.size === sizeSelect.value);
  if (!variant) return;

  form.elements.id.value = variant.id;
  showUpsellVariant(upsell, variant);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-upsell-form]');
  if (!form) return;

  event.preventDefault();
  const button = form.querySelector('button');
  button.disabled = true;

  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }] })
  });

  if (!response.ok) {
    button.textContent = (await response.json()).description || 'Nicht verfügbar';
    return;
  }

  // Für die Auswertung: Varianten aus dieser Empfehlung als unsichtbares Warenkorb-Attribut (steht später in der Bestellung)
  const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
  const added = [cart.attributes?._warenkorb_upsell, form.elements.id.value].filter(Boolean).join(',');
  await fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _warenkorb_upsell: added } })
  });

  document.dispatchEvent(new CustomEvent('cart:refresh'));
});
/* ##Custom Code Ende## */
